import React, { useState } from "react";
import { IconFolder, IconShare, IconPeople, IconEdit, IconClose } from "./Icons";
import { describeError } from "./errors";
import "./EnvironmentSidebar.css";

export default function EnvironmentSidebar({
  envs, activeEnvId, onPick, onCreate, onRename, onDelete, onShare,
}) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    const n = newName.trim();
    if (!n) return;
    setError(null);
    try {
      await onCreate(n);
      setNewName("");
      setCreating(false);
    } catch (e) {
      setError(describeError(e) || "failed to create");
    }
  };

  return (
    <div className="env-sidebar">
      <div className="env-sidebar-body">
        {envs.length === 0 && (
          <div className="env-sidebar-empty">Loading…</div>
        )}
        {envs.map((e) => {
          const owned = e.is_owner !== false;
          const isShared = !owned;
          return (
            <div
              key={e._id}
              className={`env-row ${e._id === activeEnvId ? "active" : ""} ${isShared ? "shared" : ""}`}
              title={isShared
                ? `Shared by ${e.owner_email || "another user"}`
                : (e.shared_with_everyone
                  ? "Shared with everyone"
                  : e.share_count > 0
                  ? `Shared with ${e.share_count}`
                  : undefined)}
            >
              <button
                className="env-row-main"
                onClick={() => onPick(e._id)}
              >
                <span className="env-row-icon">
                  {isShared ? <IconPeople size={12} /> : <IconFolder size={12} />}
                </span>
                <span className="env-row-name">{e.name}</span>
                {e.is_default && <span className="env-row-tag">· default</span>}
                {owned && e.shared_with_everyone && (
                  <span className="env-row-tag">· public</span>
                )}
              </button>
              {owned && !e.is_default && (
                <div className="env-row-actions">
                  <button
                    title="Share"
                    aria-label="Share environment"
                    onClick={(ev) => { ev.stopPropagation(); onShare?.(e._id); }}
                  ><IconShare size={12} /></button>
                  <button
                    title="Rename"
                    aria-label="Rename environment"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      const next = window.prompt("Rename environment", e.name);
                      if (next && next.trim()) {
                        onRename(e._id, next.trim()).catch((err) => alert(err.message));
                      }
                    }}
                  ><IconEdit size={12} /></button>
                  <button
                    title="Delete"
                    aria-label="Delete environment"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      if (window.confirm(`Delete environment "${e.name}" and all its threads/files?`)) {
                        onDelete(e._id).catch((err) => alert(err.message));
                      }
                    }}
                  ><IconClose size={12} /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="env-sidebar-footer">
        {creating ? (
          <div className="env-create">
            {error && <div className="env-create-error">{error}</div>}
            <input
              autoFocus
              className="ca-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
                if (e.key === "Escape") { setCreating(false); setNewName(""); setError(null); }
              }}
              placeholder="Environment name"
            />
            <div className="env-create-actions">
              <button
                type="button"
                className="env-create-link"
                onClick={() => { setCreating(false); setNewName(""); setError(null); }}
              >Cancel</button>
              <button
                type="button"
                className="env-create-link env-create-link--primary"
                onClick={handleCreate}
                disabled={!newName.trim()}
              >Create</button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="env-new-link"
            onClick={() => setCreating(true)}
          >+ New environment</button>
        )}
      </div>
    </div>
  );
}
